"use client";

import { authService } from "@/src/services/auth/auth.service";
import { LoginError } from "@/src/types/error.type";
import { LoggedResponse, LoginRequest } from "@/src/types/request-response.types";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

export default function Login() {

  const { register, handleSubmit } = useForm<LoginRequest>();
  const [showPassword, setShowPassword] = useState(false);
  
  const router = useRouter();

  const onSubmit = async (data: LoginRequest) => {
    try {
      const response: LoggedResponse = await authService.login(data);

      if (!response.status) {
        router.push("/dashboard?p=/error");
        return;
      }

      router.push("/dashboard");
    } catch (error: unknown) {
      const loginError = error as LoginError;
      const message =
        typeof error === "object" && error !== null && "error" in loginError &&
        typeof loginError.error?.message === "string"
          ? loginError.error.message
          : error instanceof Error
          ? error.message
          : "Login failed. Please try again.";

      if (typeof window !== "undefined") {
        window.confirm(`Login error:\n\n${message}`);
      }
    }
  }; 
  
  return (
    <div
      className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light"
      style={{
        background:
          "linear-gradient(135deg,#d8ebff 0%, #f9e5e5 100%)",
      }}
    >
      <div
        className="bg-white rounded-5 shadow-lg overflow-hidden"
        style={{
          maxWidth: "1200px",
          width: "95%",
        }}
      >
        <div className="row g-0">

          {/* Left */}
          <div className="col-lg-6 p-5 d-flex flex-column justify-content-center">

            <div className="text-center mb-5">
              <div
                className="rounded-circle bg-primary d-inline-flex justify-content-center align-items-center mb-3"
                style={{
                  width: 65,
                  height: 65,
                  color: "#fff",
                  fontSize: 30,
                }}
              >
                🔐
              </div>

              <h2 className="fw-bold">
                Login to your account!
              </h2>

              <p className="text-secondary">
                Enter your registered email address and password to login!
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>

              <div className="mb-3">
                <label className="form-label">
                  Email
                </label>

                <input
                  {...register("email")}
                  type="email"
                  className="form-control"                  
                  placeholder="eg. bramacc@gmail.com"
                  defaultValue="admin@bram2.local"
                />
              </div>

              <div className="mb-2">
                <label className="form-label">
                  Password
                </label>

                <div className="input-group">
                  <input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="********"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowPassword((current) => !current);
                    }}
                  >
                    <i className={`bi bi-eye${showPassword ? "-slash" : ""}`} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-4">

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="remember"
                  />

                  <label
                    htmlFor="remember"
                    className="form-check-label"
                  >
                    Remember me
                  </label>
                </div>

                <a href="#" className="text-decoration-none">
                  Forgot Password?
                </a>

              </div>

              <button className="btn btn-primary w-100">
                Login
              </button>

            </form>

            <div className="text-center text-secondary my-4">
              Or login with
            </div>

            <div className="row g-3">

              <div className="col-4">
                <button className="btn btn-outline-secondary w-100">
                  Google
                </button>
              </div>

              <div className="col-4">
                <button className="btn btn-outline-secondary w-100">
                  Apple
                </button>
              </div>

              <div className="col-4">
                <button className="btn btn-outline-secondary w-100">
                  Microsoft
                </button>
              </div>

            </div>

          </div>

          {/* Right */}
          <div className="col-lg-6 p-4">

            <div
              className="rounded-5 h-100 text-center d-flex flex-column justify-content-between p-5"
              style={{
                background:
                  "linear-gradient(180deg,#d9ebff,#b8d9ff)",
              }}
            >
              <div>
                <h2 className="fw-bold">
                  Write Better{" "}
                  <span className="text-primary">
                    Everywhere
                  </span>
                </h2>
              </div>

              <div className="py-5">

                {/* Replace with your own illustration */}
                <Image
                  src="/login-illustration.png"
                  alt="Illustration"
                  width={420}
                  height={420}
                  className="img-fluid"
                />

              </div>

              <div>

                <p className="text-secondary">
                  Compatible with
                  <b>
                    {" "}
                    Gmail, Outlook Web, LinkedIn
                  </b>{" "}
                  and most web editors for a smooth
                  writing experience anywhere online.
                </p>

                <div className="d-flex justify-content-center mt-4">

                  <span
                    className="bg-primary rounded-pill me-2"
                    style={{
                      width: 35,
                      height: 4,
                    }}
                  />

                  <span
                    className="bg-light rounded-pill"
                    style={{
                      width: 35,
                      height: 4,
                    }}
                  />

                </div>

              </div>

            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
